import { useState, Fragment } from "react";

const T = {
  bg: "#000000", surface: "#0A0A0A", surfaceRaised: "#111111",
  surfaceHover: "#1A1A1A", border: "#1C1C1E",
  text: "#FFFFFF", textSecondary: "#8E8E93", textTertiary: "#48484A",
  accent: "#007AFF", accentDim: "rgba(0,122,255,0.12)",
  green: "#30D158", greenDim: "rgba(48,209,88,0.12)",
  red: "#FF453A", redDim: "rgba(255,69,58,0.12)",
  orange: "#FF9F0A", orangeDim: "rgba(255,159,10,0.12)",
  purple: "#BF5AF2", purpleDim: "rgba(191,90,242,0.12)",
  cyan: "#64D2FF", cyanDim: "rgba(100,210,255,0.12)",
};

const PROJECT = {
  title: "SCM Backorder Prediction",
  subtitle: "Supply Chain Logistics with AutoML",
  description: "Predicting product backorders using LightAutoML on a highly imbalanced dataset (1:148 ratio). The automated pipeline handles preprocessing, feature selection, and model tuning — achieving 97.1% ROC-AUC where traditional models struggled to exceed 63%.",
  architecture: [
    { label: "Raw Data", sub: "1.68M × 23", icon: "db", color: T.cyan },
    { label: "LAMA Preprocess", sub: "Auto typing + Gini", icon: "gear", color: T.orange },
    { label: "Feature Select", sub: "Importance cutoff", icon: "filter", color: T.purple },
    { label: "Linear L2", sub: "Regularized", icon: "model", color: T.accent },
    { label: "LightGBM", sub: "Gradient boost", icon: "model", color: T.accent },
    { label: "CatBoost", sub: "Cat. boost", icon: "model", color: T.accent },
    { label: "Ensemble", sub: "Weighted blend", icon: "merge", color: T.green },
    { label: "Output", sub: "Yes / No", icon: "out", color: T.green },
  ],
  hyperparameters: [
    { key: "task", value: "binary", type: "str" },
    { key: "timeout", value: "7200s", type: "num" },
    { key: "n_folds", value: "5", type: "num" },
    { key: "n_threads", value: "8", type: "num" },
    { key: "weight_0", value: "0.50", type: "num" },
    { key: "weight_1", value: "74.73", type: "num" },
    { key: "random_state", value: "42", type: "num" },
    { key: "test_size", value: "0.2", type: "num" },
  ],
  metrics: {
    rocAuc: 97.1, f1: 69.95, precision: 70.55, recall: 69.4,
    trainingLoss: [
      { epoch: 1, loss: 0.82, val: 0.79 }, { epoch: 5, loss: 0.64, val: 0.63 },
      { epoch: 10, loss: 0.51, val: 0.52 }, { epoch: 15, loss: 0.42, val: 0.44 },
      { epoch: 20, loss: 0.36, val: 0.39 }, { epoch: 25, loss: 0.31, val: 0.35 },
      { epoch: 30, loss: 0.27, val: 0.33 }, { epoch: 35, loss: 0.24, val: 0.31 },
      { epoch: 40, loss: 0.22, val: 0.29 }, { epoch: 45, loss: 0.19, val: 0.28 },
      { epoch: 50, loss: 0.17, val: 0.27 },
    ],
    confusionMatrix: { labels: ["No Backorder", "Backorder"], matrix: [[1635200, 8420], [4180, 7660]] },
    featureImportance: [
      { feature: "national_inv", importance: 2.45 },
      { feature: "forecast_3_month", importance: 1.32 },
      { feature: "forecast_6_month", importance: 1.05 },
      { feature: "perf_12_month_avg", importance: 0.87 },
      { feature: "sales_1_month", importance: 0.82 },
      { feature: "lead_time", importance: 0.78 },
      { feature: "sales_3_month", importance: 0.72 },
      { feature: "forecast_9_month", importance: 0.68 },
      { feature: "sales_6_month", importance: 0.61 },
      { feature: "min_bank", importance: 0.55 },
      { feature: "sales_9_month", importance: 0.48 },
      { feature: "pieces_past_due", importance: 0.35 },
      { feature: "in_transit_qty", importance: 0.31 },
      { feature: "local_bo_qty", importance: 0.22 },
      { feature: "perf_6_month_avg", importance: 0.18 },
    ],
    comparativeResults: [
      { model: "LightAutoML", f1: 69.95, roc: 97.1, time: 66 },
      { model: "Random Forest", f1: 35, roc: 63, time: 5 },
      { model: "Logistic Reg.", f1: 12, roc: 55, time: 3 },
      { model: "SVM", f1: 1, roc: 50, time: 90 },
      { model: "kNN", f1: 1, roc: 56, time: 183 },
    ],
    thresholdAnalysis: [
      { threshold: 0.5, precision: 62.11, recall: 81.38, f1: 67.31 },
      { threshold: 0.8, precision: 70.55, recall: 69.4, f1: 69.95 },
    ],
  },
  tags: ["Python", "LightAutoML", "LightGBM", "CatBoost", "scikit-learn"],
  notebookUrl: "https://colab.research.google.com/",
  reportUrl: "#",
  datasetUrl: "https://www.kaggle.com/",
};

const iconPaths = {
  db: (c) => <><ellipse cx="8" cy="4" rx="5.5" ry="2.2" stroke={c} strokeWidth="1.2" fill="none"/><path d="M2.5 4v3.5c0 1.2 2.5 2.2 5.5 2.2s5.5-1 5.5-2.2V4" stroke={c} strokeWidth="1.2" fill="none"/><path d="M2.5 7.5v3.5c0 1.2 2.5 2.2 5.5 2.2s5.5-1 5.5-2.2V7.5" stroke={c} strokeWidth="1.2" fill="none" opacity=".5"/></>,
  gear: (c) => <><circle cx="8" cy="8" r="2.2" stroke={c} strokeWidth="1.2" fill="none"/><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.3 3.3l1.4 1.4M11.3 11.3l1.4 1.4M3.3 12.7l1.4-1.4M11.3 4.7l1.4-1.4" stroke={c} strokeWidth="1.1" strokeLinecap="round"/></>,
  filter: (c) => <path d="M2.5 3h11L9 8.5v3.5l-2 1V8.5L2.5 3z" stroke={c} strokeWidth="1.2" fill="none" strokeLinejoin="round"/>,
  model: (c) => <><rect x="3" y="3" width="10" height="10" rx="2" stroke={c} strokeWidth="1.2" fill="none"/><path d="M6 6h4M6 8h4M6 10h2" stroke={c} strokeWidth="1.1" strokeLinecap="round"/></>,
  merge: (c) => <><path d="M4 4.5v3c0 2 4 4 4 4s4-2 4-4v-3" stroke={c} strokeWidth="1.2" fill="none" strokeLinecap="round"/><circle cx="4" cy="3.5" r="1.3" stroke={c} strokeWidth="1.2" fill="none"/><circle cx="12" cy="3.5" r="1.3" stroke={c} strokeWidth="1.2" fill="none"/><circle cx="8" cy="12.5" r="1.3" stroke={c} strokeWidth="1.2" fill="none"/></>,
  out: (c) => <><path d="M8 2.5v7M5.5 7l2.5 2.5L10.5 7" stroke={c} strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 12.5h10" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></>,
};

const Icon = ({ type, color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">{iconPaths[type]?.(color)}</svg>
);

const mono = "'SF Mono','Fira Code','Cascadia Code','Consolas',monospace";
const sans = "-apple-system,BlinkMacSystemFont,'SF Pro Display','Segoe UI',system-ui,sans-serif";

const css = `
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
@keyframes growBar{from{width:0}}
@keyframes scaleIn{from{opacity:0;transform:scale(.85)}to{opacity:1;transform:scale(1)}}
@keyframes dashDraw{from{stroke-dashoffset:600}to{stroke-dashoffset:0}}
*{box-sizing:border-box;margin:0;padding:0}
::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:${T.border};border-radius:4px}
.tab-btn:hover{background:${T.surfaceHover}!important}
.pn{transition:border-color .2s,transform .2s,box-shadow .2s}
.pn:hover{border-color:${T.accent}55!important;transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,122,255,.08)}
.chip:hover{border-color:${T.textTertiary}!important}
.rl{transition:border-color .2s,transform .15s,box-shadow .2s}
.rl:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(0,0,0,.3)}
.br{transition:all .15s}
.br:hover .bl{color:${T.text}!important}
.br:hover .bf{filter:brightness(1.3)}
.cc{transition:border-color .15s,transform .15s}
.cc:hover{transform:scale(1.04)}
.cr{transition:background .15s}
.cr:hover{background:${T.surfaceHover}}
`;

const SectionLabel = ({ children, delay = 0 }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: T.textSecondary, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, animation: `fadeUp .4s ease ${delay}s both` }}>
    <span style={{ width: 3, height: 12, borderRadius: 2, background: T.accent, display: "inline-block" }}/>
    {children}
  </div>
);

const Tag = ({ children, color = T.accent, bg = T.accentDim }) => (
  <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: mono, color, background: bg, border: `1px solid ${color}22`, whiteSpace: "nowrap" }}>{children}</span>
);

const MetricCard = ({ label, value, unit, color = T.accent, delay = 0 }) => (
  <div style={{ flex: "1 1 0", minWidth: 90, padding: "16px 12px", borderRadius: 12, background: T.surfaceRaised, border: `1px solid ${T.border}`, textAlign: "center", animation: `fadeUp .45s ease ${delay}s both` }}>
    <div style={{ fontSize: 9, color: T.textSecondary, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color, fontFamily: mono, lineHeight: 1 }}>
      {value}{unit && <span style={{ fontSize: 12, color: T.textSecondary, marginLeft: 2 }}>{unit}</span>}
    </div>
  </div>
);

const ArchitecturePipeline = ({ steps }) => (
  <div style={{ overflowX: "auto", paddingBottom: 8 }}>
    <div style={{ display: "flex", alignItems: "center", minWidth: "max-content" }}>
      {steps.map((s, i) => (
        <Fragment key={i}>
          <div className="pn" style={{ width: 100, padding: "12px 6px 10px", borderRadius: 12, background: T.surfaceRaised, border: `1px solid ${T.border}`, textAlign: "center", cursor: "default", animation: `scaleIn .35s ease ${i * .06}s both` }}>
            <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}><Icon type={s.icon} color={s.color}/></div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 3 }}>{s.label}</div>
            <div style={{ fontSize: 8, color: T.textSecondary, lineHeight: 1.3 }}>{s.sub}</div>
          </div>
          {i < steps.length - 1 && (
            <svg width="24" height="12" viewBox="0 0 24 12" style={{ flexShrink: 0, animation: `fadeIn .3s ease ${i * .06 + .1}s both` }}>
              <line x1="0" y1="6" x2="16" y2="6" stroke={T.textTertiary} strokeWidth="1.2"/>
              <path d="M14 2.5l5.5 3.5-5.5 3.5" fill="none" stroke={T.accent} strokeWidth="1.3" strokeOpacity=".6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </Fragment>
      ))}
    </div>
  </div>
);

const LossChart = ({ data }) => {
  const [hover, setHover] = useState(null);
  const W = 540, H = 230, pad = { t: 24, r: 20, b: 34, l: 46 };
  const w = W - pad.l - pad.r, h = H - pad.t - pad.b;
  const maxL = Math.max(...data.flatMap(d => [d.loss, d.val])) * 1.1;
  const maxE = Math.max(...data.map(d => d.epoch));
  const x = e => pad.l + (e / maxE) * w;
  const y = v => pad.t + (1 - v / maxL) * h;
  const ln = key => data.map((d, i) => `${i ? "L" : "M"}${x(d.epoch).toFixed(1)},${y(d[key]).toFixed(1)}`).join(" ");
  return (
    <div style={{ background: T.surfaceRaised, borderRadius: 14, border: `1px solid ${T.border}`, padding: "14px 10px 6px", animation: "fadeUp .4s ease both" }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <defs><linearGradient id="lf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.accent} stopOpacity=".18"/><stop offset="100%" stopColor={T.accent} stopOpacity="0"/></linearGradient></defs>
        {[0,.2,.4,.6,.8].map(v=><g key={v}><line x1={pad.l} y1={y(v*maxL)} x2={W-pad.r} y2={y(v*maxL)} stroke={T.border} strokeWidth=".5"/><text x={pad.l-6} y={y(v*maxL)+3} textAnchor="end" fill={T.textTertiary} fontSize="8.5" fontFamily={mono}>{(v*maxL).toFixed(2)}</text></g>)}
        <text x={W/2} y={H-5} textAnchor="middle" fill={T.textTertiary} fontSize="9" fontFamily={mono}>Epoch</text>
        <path d={`${ln("loss")} L${x(maxE)},${y(0)} L${x(data[0].epoch)},${y(0)} Z`} fill="url(#lf)"/>
        <path d={ln("loss")} fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{strokeDasharray:600,animation:"dashDraw 1.2s ease both"}}/>
        <path d={ln("val")} fill="none" stroke={T.orange} strokeWidth="2" strokeDasharray="5 3" strokeLinecap="round" strokeLinejoin="round" style={{animation:"fadeIn .8s ease .4s both"}}/>
        {data.map((d,i)=>(
          <g key={i} onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(null)} style={{cursor:"crosshair"}}>
            <rect x={x(d.epoch)-16} y={pad.t-5} width="32" height={h+10} fill="transparent"/>
            {hover===i&&<>
              <line x1={x(d.epoch)} y1={pad.t} x2={x(d.epoch)} y2={pad.t+h} stroke={T.textTertiary} strokeWidth=".5" strokeDasharray="3 2"/>
              <circle cx={x(d.epoch)} cy={y(d.loss)} r="4.5" fill={T.accent} stroke={T.bg} strokeWidth="2"/>
              <circle cx={x(d.epoch)} cy={y(d.val)} r="4.5" fill={T.orange} stroke={T.bg} strokeWidth="2"/>
              <rect x={x(d.epoch)-62} y={Math.min(y(d.loss),y(d.val))-42} width="124" height="34" rx="7" fill={T.surfaceRaised} stroke={T.border}/>
              <text x={x(d.epoch)} y={Math.min(y(d.loss),y(d.val))-22} textAnchor="middle" fill={T.text} fontSize="9.5" fontFamily={mono} fontWeight="600">{`E${d.epoch}  T:${d.loss.toFixed(3)}  V:${d.val.toFixed(3)}`}</text>
            </>}
          </g>
        ))}
        <g transform={`translate(${pad.l+6},${pad.t+2})`}>
          <line x1="0" y1="0" x2="14" y2="0" stroke={T.accent} strokeWidth="2"/><text x="18" y="3.5" fill={T.textSecondary} fontSize="9" fontFamily={mono}>Train</text>
          <line x1="56" y1="0" x2="70" y2="0" stroke={T.orange} strokeWidth="2" strokeDasharray="4 3"/><text x="74" y="3.5" fill={T.textSecondary} fontSize="9" fontFamily={mono}>Val</text>
        </g>
      </svg>
    </div>
  );
};

const ConfusionMatrix = ({ matrix, labels }) => {
  const [hover, setHover] = useState(null);
  const flatMax = Math.max(...matrix.flat());
  const fmt = n => n>=1e6?(n/1e6).toFixed(2)+"M":n>=1e3?(n/1e3).toFixed(1)+"K":n+"";
  const gc = v => { const n=v/flatMax; if(n>.5)return{bg:T.accent,text:"#fff"};if(n>.01)return{bg:T.accentDim,text:T.accent};return{bg:T.redDim,text:T.red}; };
  return (
    <div style={{display:"inline-block",maxWidth:"100%",overflowX:"auto",animation:"fadeUp .4s ease both"}}>
      <div style={{display:"grid",gridTemplateColumns:"80px 1fr 1fr",gap:4,alignItems:"center"}}>
        <div/>
        {labels.map((l,i)=><div key={i} style={{fontSize:9,color:T.textSecondary,textAlign:"center",fontWeight:600,fontFamily:mono,padding:"0 0 4px"}}>Pred: {l.split(" ").pop()}</div>)}
        {matrix.map((row,ri)=>(
          <Fragment key={ri}>
            <div style={{fontSize:9,color:T.textSecondary,fontWeight:600,fontFamily:mono,textAlign:"right",paddingRight:8}}>True: {labels[ri].split(" ").pop()}</div>
            {row.map((val,ci)=>{const c=gc(val);const hov=hover?.[0]===ri&&hover?.[1]===ci;return(
              <div key={ci} className="cc" onMouseEnter={()=>setHover([ri,ci])} onMouseLeave={()=>setHover(null)}
                style={{width:110,height:78,borderRadius:10,background:c.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"default",border:hov?`1.5px solid ${T.accent}`:"1.5px solid transparent",animation:`scaleIn .35s ease ${(ri*2+ci)*.08}s both`}}>
                <div style={{fontSize:22,fontWeight:800,color:c.text,fontFamily:mono}}>{fmt(val)}</div>
                {hov&&<div style={{fontSize:9,color:c.text,opacity:.7,marginTop:2,fontFamily:mono}}>{val.toLocaleString()}</div>}
              </div>
            );})}
          </Fragment>
        ))}
      </div>
      <div style={{textAlign:"center",marginTop:8}}><span style={{fontSize:9,color:T.textTertiary,fontFamily:mono}}>← Predicted →</span></div>
    </div>
  );
};

const FeatureImportance = ({ data, maxBars = 12 }) => {
  const [hi, setHi] = useState(null);
  const sl = data.slice(0, maxBars);
  const mx = sl[0]?.importance || 1;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {sl.map((d,i)=>{const pct=(d.importance/mx)*100;const hov=hi===i;return(
        <div key={d.feature} className="br" onMouseEnter={()=>setHi(i)} onMouseLeave={()=>setHi(null)}
          style={{display:"flex",alignItems:"center",gap:8,cursor:"default",animation:`slideIn .3s ease ${i*.03}s both`}}>
          <div className="bl" style={{width:120,fontSize:10,fontFamily:mono,color:hov?T.text:T.textSecondary,textAlign:"right",flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",transition:"color .15s"}}>{d.feature}</div>
          <div style={{flex:1,height:16,background:T.surfaceRaised,borderRadius:4,overflow:"hidden",position:"relative"}}>
            <div className="bf" style={{height:"100%",width:`${pct}%`,borderRadius:4,background:hov?`linear-gradient(90deg,${T.accent},${T.cyan})`:`linear-gradient(90deg,${T.accent}cc,${T.accent}44)`,transition:"background .2s,filter .2s",animation:`growBar .5s ease ${i*.04+.15}s both`}}/>
            {hov&&<div style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",fontSize:9,fontWeight:700,color:T.text,fontFamily:mono}}>{d.importance.toFixed(2)}</div>}
          </div>
        </div>
      );})}
    </div>
  );
};

const ComparativeTable = ({ data }) => {
  const best = data.reduce((a,b)=>a.f1>b.f1?a:b);
  return (
    <div style={{overflowX:"auto",animation:"fadeUp .4s ease both"}}>
      <table style={{width:"100%",borderCollapse:"separate",borderSpacing:"0 3px",fontFamily:mono,fontSize:11}}>
        <thead><tr>{["Model","F1 (%)","ROC-AUC (%)","Time (min)"].map(h=><th key={h} style={{textAlign:h==="Model"?"left":"right",padding:"8px 12px",color:T.textTertiary,fontWeight:600,fontSize:9,letterSpacing:".08em",textTransform:"uppercase",borderBottom:`1px solid ${T.border}`}}>{h}</th>)}</tr></thead>
        <tbody>{data.map((row,i)=>{const isBest=row.model===best.model;return(
          <tr key={row.model} className="cr" style={{background:isBest?T.accentDim:"transparent",animation:`fadeIn .3s ease ${i*.06}s both`}}>
            <td style={{padding:"10px 12px",color:isBest?T.accent:T.text,fontWeight:isBest?700:500,borderRadius:"8px 0 0 8px"}}>{row.model}{isBest&&<span style={{marginLeft:8,fontSize:9,color:T.green,fontWeight:700}}>★ BEST</span>}</td>
            <td style={{padding:"10px 12px",textAlign:"right",color:isBest?T.accent:T.text}}>{row.f1}</td>
            <td style={{padding:"10px 12px",textAlign:"right",color:isBest?T.accent:T.text}}>{row.roc}</td>
            <td style={{padding:"10px 12px",textAlign:"right",color:T.textSecondary,borderRadius:"0 8px 8px 0"}}>{row.time}</td>
          </tr>
        );})}</tbody>
      </table>
    </div>
  );
};

const ThresholdComparison = ({ data }) => (
  <div style={{display:"flex",gap:10,flexWrap:"wrap",animation:"fadeUp .4s ease .1s both"}}>
    {data.map((d,i)=>(
      <div key={d.threshold} style={{flex:"1 1 200px",padding:16,borderRadius:12,background:T.surfaceRaised,border:`1px solid ${i===1?T.accent+"44":T.border}`,position:"relative"}}>
        {i===1&&<div style={{position:"absolute",top:-8,right:12,background:T.accent,color:"#fff",padding:"2px 8px",borderRadius:4,fontSize:9,fontWeight:700}}>SELECTED</div>}
        <div style={{fontSize:9,color:T.textSecondary,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",marginBottom:10}}>Threshold: {d.threshold}</div>
        <div style={{display:"flex",gap:16}}>
          {[["Pcs",d.precision,T.orange],["Rec",d.recall,T.purple],["F1",d.f1,T.green]].map(([l,v,c])=>(
            <div key={l}><div style={{fontSize:8,color:T.textTertiary,fontWeight:600,marginBottom:3,fontFamily:mono}}>{l}</div><div style={{fontSize:16,fontWeight:700,color:c,fontFamily:mono}}>{v}%</div></div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ROCCurve = () => {
  const [hover, setHover] = useState(null);
  const W=220,H=220,pad=36;
  const pts=[[0,0],[.02,.55],[.05,.72],[.1,.82],[.15,.88],[.25,.92],[.35,.95],[.5,.97],[.7,.985],[1,1]];
  const x=v=>pad+v*(W-pad*2), y=v=>H-pad-v*(H-pad*2);
  const path=pts.map((p,i)=>`${i?"L":"M"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ");
  return(
    <div style={{background:T.surfaceRaised,borderRadius:14,border:`1px solid ${T.border}`,padding:12,display:"inline-block",animation:"fadeUp .4s ease .15s both"}}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <line x1={pad} y1={y(0)} x2={pad} y2={y(1)} stroke={T.border} strokeWidth=".5"/>
        <line x1={pad} y1={y(0)} x2={x(1)} y2={y(0)} stroke={T.border} strokeWidth=".5"/>
        <line x1={pad} y1={y(0)} x2={x(1)} y2={y(1)} stroke={T.textTertiary} strokeWidth="1" strokeDasharray="4 3" opacity=".4"/>
        <defs><linearGradient id="rf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.accent} stopOpacity=".12"/><stop offset="100%" stopColor={T.accent} stopOpacity="0"/></linearGradient></defs>
        <path d={`${path} L${x(1)},${y(0)} L${x(0)},${y(0)} Z`} fill="url(#rf)"/>
        <path d={path} fill="none" stroke={T.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p,i)=>(
          <g key={i} onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(null)} style={{cursor:"crosshair"}}>
            <circle cx={x(p[0])} cy={y(p[1])} r="8" fill="transparent"/>
            {hover===i&&<><circle cx={x(p[0])} cy={y(p[1])} r="4" fill={T.accent} stroke={T.bg} strokeWidth="2"/><rect x={x(p[0])-38} y={y(p[1])-26} width="76" height="18" rx="4" fill={T.surfaceRaised} stroke={T.border}/><text x={x(p[0])} y={y(p[1])-14} textAnchor="middle" fill={T.text} fontSize="8" fontFamily={mono} fontWeight="600">FPR:{p[0]} TPR:{p[1]}</text></>}
          </g>
        ))}
        <text x={W/2} y={H-8} textAnchor="middle" fill={T.textTertiary} fontSize="8.5" fontFamily={mono}>False Positive Rate</text>
        <text x={10} y={H/2} textAnchor="middle" fill={T.textTertiary} fontSize="8.5" fontFamily={mono} transform={`rotate(-90 10 ${H/2})`}>True Positive Rate</text>
        <text x={W-pad-2} y={pad+14} textAnchor="end" fill={T.accent} fontSize="10" fontFamily={mono} fontWeight="700">AUC: 0.971</text>
      </svg>
    </div>
  );
};

const HyperparamChips = ({ params }) => (
  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
    {params.map((p,i)=>(
      <div key={p.key} className="chip" style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:8,background:T.surfaceRaised,border:`1px solid ${T.border}`,fontSize:11,fontFamily:mono,animation:`fadeIn .3s ease ${i*.03}s both`}}>
        <span style={{color:T.textSecondary}}>{p.key}</span><span style={{color:T.textTertiary}}>:</span>
        <span style={{color:p.type==="str"?T.green:T.orange,fontWeight:600}}>{p.type==="str"?`"${p.value}"`:p.value}</span>
      </div>
    ))}
  </div>
);

const ReproduceSection = ({ notebookUrl, reportUrl, datasetUrl }) => {
  const links=[
    {label:"Open Notebook",url:notebookUrl,color:T.orange,icon:"📓"},
    {label:"Download Report",url:reportUrl,color:T.accent,icon:"📄"},
    {label:"View Dataset",url:datasetUrl,color:T.green,icon:"📊"},
  ];
  return(
    <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
      {links.map((l,i)=>(
        <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" className="rl"
          style={{display:"inline-flex",alignItems:"center",gap:8,padding:"10px 18px",borderRadius:10,background:T.surfaceRaised,border:`1px solid ${T.border}`,color:l.color,fontSize:12,fontWeight:600,textDecoration:"none",cursor:"pointer",fontFamily:sans,animation:`fadeUp .35s ease ${i*.08}s both`}}>
          <span style={{fontSize:15}}>{l.icon}</span>{l.label}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{opacity:.4}}><path d="M5 1H2.5A1.5 1.5 0 001 2.5v7A1.5 1.5 0 002.5 11h7A1.5 1.5 0 0011 9.5V7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><path d="M7 1h4v4M11 1L5.5 6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </a>
      ))}
    </div>
  );
};

const TABS = ["Overview","Training","Evaluation","Reproduce"];
const TabBar = ({ active, onChange }) => (
  <div style={{display:"flex",gap:2,padding:3,borderRadius:10,background:T.surfaceRaised,border:`1px solid ${T.border}`,width:"fit-content"}}>
    {TABS.map(tab=>(
      <button key={tab} className="tab-btn" onClick={()=>onChange(tab)}
        style={{padding:"7px 16px",borderRadius:8,border:"none",fontSize:12,fontWeight:600,cursor:"pointer",background:active===tab?T.accent:"transparent",color:active===tab?"#fff":T.textSecondary,fontFamily:sans,transition:"all .2s"}}>
        {tab}
      </button>
    ))}
  </div>
);

export default function MLModelView() {
  const [tab, setTab] = useState("Overview");
  const [tabKey, setTabKey] = useState(0);
  const p = PROJECT, m = p.metrics;
  const changeTab = t => { setTab(t); setTabKey(k=>k+1); };

  return (
    <div style={{width:"100%",minHeight:"100%",background:T.bg,color:T.text,fontFamily:sans,WebkitFontSmoothing:"antialiased"}}>
      <style>{css}</style>
      <div style={{maxWidth:896,margin:"0 auto",padding:"28px 24px 0"}}>
        <div style={{animation:"fadeUp .4s ease both"}}>
          <h1 style={{fontSize:24,fontWeight:800,letterSpacing:"-.025em",margin:"0 0 4px",lineHeight:1.2}}>{p.title}</h1>
          <p style={{fontSize:13,color:T.textSecondary,margin:"0 0 14px"}}>{p.subtitle}</p>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:20,animation:"fadeUp .4s ease .05s both"}}>
          {p.tags.map(t=><Tag key={t}>{t}</Tag>)}
        </div>
        <div style={{animation:"fadeUp .4s ease .1s both"}}><TabBar active={tab} onChange={changeTab}/></div>
      </div>

      <div key={tabKey} style={{maxWidth:896,margin:"0 auto",padding:24}}>
        {tab==="Overview"&&(
          <div style={{display:"flex",flexDirection:"column",gap:30}}>
            <div><SectionLabel>About</SectionLabel><p style={{fontSize:13,color:T.textSecondary,lineHeight:1.75,maxWidth:660,animation:"fadeUp .4s ease .05s both"}}>{p.description}</p></div>
            <div><SectionLabel delay={.08}>Model Architecture</SectionLabel><ArchitecturePipeline steps={p.architecture}/></div>
            <div><SectionLabel delay={.14}>Hyperparameters</SectionLabel><HyperparamChips params={p.hyperparameters}/></div>
            <div><SectionLabel delay={.18}>Key Metrics</SectionLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                <MetricCard label="ROC-AUC" value={m.rocAuc} unit="%" color={T.accent} delay={.2}/>
                <MetricCard label="F1 Score" value={m.f1} unit="%" color={T.green} delay={.24}/>
                <MetricCard label="Precision" value={m.precision} unit="%" color={T.orange} delay={.28}/>
                <MetricCard label="Recall" value={m.recall} unit="%" color={T.purple} delay={.32}/>
              </div>
            </div>
          </div>
        )}
        {tab==="Training"&&(
          <div style={{display:"flex",flexDirection:"column",gap:30}}>
            <div><SectionLabel>Loss Curve</SectionLabel><LossChart data={m.trainingLoss}/></div>
            <div><SectionLabel delay={.1}>Threshold Analysis</SectionLabel><ThresholdComparison data={m.thresholdAnalysis}/></div>
            <div><SectionLabel delay={.15}>Training Configuration</SectionLabel><HyperparamChips params={p.hyperparameters}/></div>
          </div>
        )}
        {tab==="Evaluation"&&(
          <div style={{display:"flex",flexDirection:"column",gap:30}}>
            <div style={{display:"flex",flexWrap:"wrap",gap:24,alignItems:"flex-start"}}>
              <div><SectionLabel>Confusion Matrix</SectionLabel><ConfusionMatrix matrix={m.confusionMatrix.matrix} labels={m.confusionMatrix.labels}/></div>
              <div><SectionLabel>ROC Curve</SectionLabel><ROCCurve/></div>
            </div>
            <div><SectionLabel delay={.1}>Feature Importance</SectionLabel><FeatureImportance data={m.featureImportance}/></div>
            <div><SectionLabel delay={.16}>Comparative Analysis</SectionLabel><ComparativeTable data={m.comparativeResults}/></div>
          </div>
        )}
        {tab==="Reproduce"&&(
          <div style={{display:"flex",flexDirection:"column",gap:30}}>
            <div>
              <SectionLabel>Reproduce This Experiment</SectionLabel>
              <p style={{fontSize:13,color:T.textSecondary,lineHeight:1.7,margin:"0 0 16px",maxWidth:560,animation:"fadeUp .4s ease .05s both"}}>Everything you need to reproduce or extend this work. The notebook runs on Google Colab with a free GPU runtime.</p>
              <ReproduceSection notebookUrl={p.notebookUrl} reportUrl={p.reportUrl} datasetUrl={p.datasetUrl}/>
            </div>
            <div><SectionLabel delay={.1}>Dataset Summary</SectionLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                <MetricCard label="Total Rows" value="1.69M" color={T.text} delay={.12}/>
                <MetricCard label="Features" value="23" color={T.cyan} delay={.16}/>
                <MetricCard label="Class Ratio" value="1:148" color={T.red} delay={.2}/>
                <MetricCard label="Target" value="Binary" color={T.green} delay={.24}/>
              </div>
            </div>
            <div><SectionLabel delay={.2}>Stack</SectionLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,animation:"fadeUp .4s ease .22s both"}}>
                {["Python 3.8+","LightAutoML","scikit-learn","pandas","numpy","matplotlib","Google Colab"].map(s=><Tag key={s} color={T.textSecondary} bg={T.surfaceRaised}>{s}</Tag>)}
              </div>
            </div>
            <div><SectionLabel delay={.26}>Dataset Columns</SectionLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:4,animation:"fadeUp .4s ease .28s both"}}>
                {["sku","national_inv","lead_time","in_transit_qty","forecast_3_month","forecast_6_month","forecast_9_month","sales_1_month","sales_3_month","sales_6_month","sales_9_month","min_bank","potential_issue","pieces_past_due","perf_6_month_avg","perf_12_month_avg","local_bo_qty","deck_risk","oe_constraint","ppap_risk","stop_auto_buy","rev_stop","went_on_backorder"].map(c=>(
                  <span key={c} style={{fontSize:9,fontFamily:mono,color:T.textTertiary,background:T.surface,border:`1px solid ${T.border}`,padding:"2px 7px",borderRadius:4}}>{c}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
